import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, X } from 'lucide-react';
const GOOGLE_REVIEW_URL = 'https://g.page/r/Cat9FuHdU1RAEAE/review';
interface MilestoneReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  milestone: 20 | 50 | 100;
  scanCount: number;
}
export function MilestoneReviewModal({
  isOpen,
  onClose,
  milestone,
  scanCount
}: MilestoneReviewModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const handleLeaveReview = () => {
    window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener,noreferrer');
    onClose();
  };
  const getMilestoneData = () => {
    switch (milestone) {
      case 20:
        return {
          icon: '🔍',
          gradient: 'from-blue-500 to-blue-700',
          color: 'text-blue-500',
          borderColor: 'border-blue-500',
          bgHover: 'hover:bg-blue-600',
          bgColor: 'bg-blue-500',
          title: 'Dedicated Analyst',
          rank: 'LEVEL 2 CLEARANCE',
          body: "You've completed 20 forensic scans — your dedication to uncovering the truth is remarkable.",
          cta: 'Would you share your experience on Google? It helps others find the clarity they need.'
        };
      case 50:
        return {
          icon: '🛡️',
          gradient: 'from-purple-500 to-purple-700',
          color: 'text-purple-500',
          borderColor: 'border-purple-500',
          bgHover: 'hover:bg-purple-600',
          bgColor: 'bg-purple-500',
          title: 'Expert Investigator',
          rank: 'LEVEL 3 CLEARANCE',
          body: "50 scans completed! You're becoming a true expert at identifying toxic communication patterns.",
          cta: 'Your expertise matters. A Google review helps victims find this tool faster.'
        };
      case 100:
      default:
        return {
          icon: '⭐',
          gradient: 'from-yellow-400 to-yellow-600',
          color: 'text-yellow-500',
          borderColor: 'border-yellow-500',
          bgHover: 'hover:bg-yellow-600',
          bgColor: 'bg-yellow-500',
          title: 'Master Detective',
          rank: 'MAXIMUM CLEARANCE',
          body: "100 scans — extraordinary! You're a forensic communication master with unmatched awareness.",
          cta: "You've seen the impact firsthand. Share your journey on Google to help others."
        };
    }
  };
  const data = getMilestoneData();
  return <AnimatePresence data-id="element-2874">
      {isOpen && <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4" data-id="element-2875">
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/80 backdrop-blur-sm" data-id="element-2876" />

          {/* Modal Card */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="relative bg-[#050505] border border-gray-800 max-w-md w-full p-8 text-center shadow-2xl overflow-hidden" data-id="element-2877">
            {/* Corner Brackets */}
            <div className={`absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 ${data.borderColor}`} data-id="element-2878" />
            <div className={`absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 ${data.borderColor}`} data-id="element-2879" />
            <div className={`absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 ${data.borderColor}`} data-id="element-2880" />
            <div className={`absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 ${data.borderColor}`} data-id="element-2881" />

            {/* Scanning Line Animation */}
            <div className={`absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-current to-transparent ${data.color} animate-[scan_3s_ease-in-out_infinite]`} data-id="element-2882" />
            <style data-id="element-2883">{`
              @keyframes scan {
                0%, 100% { transform: translateX(-100%); }
                50% { transform: translateX(100%); }
              }
            `}</style>

            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors p-2 z-10" aria-label="Close" data-id="element-2884">
              <X size={20} data-id="element-2885" />
            </button>

            {/* Badge */}
            <div className="flex justify-center mb-4" data-id="element-2886">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${data.gradient} flex items-center justify-center text-3xl shadow-lg`} data-id="element-2887">
                {data.icon}
              </div>
            </div>

            {/* Rank Badge */}
            <div className="mb-4" data-id="element-2888">
              <span className={`inline-block px-3 py-1 border ${data.borderColor} ${data.color} text-[10px] font-mono font-bold uppercase tracking-widest bg-black/50`} data-id="element-2889">
                {data.rank}
              </span>
            </div>

            {/* Star Rating */}
            <div className="text-2xl tracking-[4px] mb-4" data-id="element-2890">⭐⭐⭐⭐⭐</div>

            {/* Title */}
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2" data-id="element-2891">
              {data.title}
            </h2>

            {/* Scan Count */}
            <p className="text-white font-bold mb-3" data-id="element-2892">
              You've completed {scanCount} scans
            </p>

            {/* Body */}
            <p className="text-gray-400 text-sm leading-relaxed mb-4" data-id="element-2893">
              {data.body}
            </p>

            {/* CTA */}
            <p className="text-gray-300 text-sm font-medium mb-8" data-id="element-2894">{data.cta}</p>

            {/* Primary Button */}
            <button onClick={handleLeaveReview} className={`w-full py-3 px-6 ${data.bgColor} ${data.bgHover} text-white font-bold rounded-none flex items-center justify-center gap-2 uppercase tracking-wider text-sm transition-colors mb-3`} data-id="element-2895">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white" data-id="element-2896">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-2897" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-2898" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-2899" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-2900" />
              </svg>
              Leave a Google Review
            </button>

            {/* Secondary Button */}
            <button onClick={onClose} className="w-full py-3 px-6 bg-transparent border border-gray-700 text-gray-400 font-bold hover:text-gray-300 hover:border-gray-500 uppercase tracking-wider text-sm transition-colors mb-6" data-id="element-2901">
              Maybe Later
            </button>

            {/* Footer */}
            <div className="flex items-center justify-center gap-2 text-gray-600 text-xs font-mono" data-id="element-2902">
              <Shield size={12} data-id="element-2903" />
              <span data-id="element-2904">Your privacy is always protected</span>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}